import React, { useCallback, useContext, useEffect, useState } from "react";

import {
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

import { ContractorCompanySummary } from "@safetyculture/s12-apis-connect-web/s12/contractors/admin/v1/contractors_pb";
import { ContractorCompanyStatus } from "@safetyculture/s12-apis-connect-web/s12/contractors/v1/contractor_company_status_pb";

import { listContractorCompanySummaries } from "api/endpoints/contractors";
import { callOptionsHeaders } from "api/helpers";
import EmptyTableResults from "components/shared/table/empty-table-results";
import RPCTablePaginationActions from "components/shared/table/rpc-table-pagination-actions";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

const PAGE_SIZE = 50;

const statusLabel = (status: ContractorCompanyStatus): string => {
  switch (status) {
    case ContractorCompanyStatus.ACTIVE:
      return "Active";
    case ContractorCompanyStatus.PENDING:
      return "Pending";
    default:
      return "Unknown";
  }
};

interface OrgContractorsTabProps {
  roleId: string;
  regionCode: string;
}

const OrgContractorsTab = ({ roleId, regionCode }: OrgContractorsTabProps) => {
  const { showError } = useContext(SnackbarContext);

  const [summaries, setSummaries] = useState<ContractorCompanySummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [nextPageToken, setNextPageToken] = useState("");
  const [pageTokens, setPageTokens] = useState<string[]>([""]);
  const [totalCount, setTotalCount] = useState(0);

  const fetchData = useCallback(
    async (pageToken?: string) => {
      const headers = callOptionsHeaders(regionCode, roleId);
      try {
        setIsLoading(true);

        const res = await listContractorCompanySummaries(
          { orgId: roleId, pageSize: PAGE_SIZE, pageToken },
          headers
        );

        setSummaries(res.response?.summaries ?? []);
        setNextPageToken(res.response?.nextPageToken ?? "");
        setTotalCount(res.response?.totalCount ?? 0);
      } catch (e) {
        showError(e as GrpcError);
      } finally {
        setIsLoading(false);
      }
    },
    [regionCode, roleId, showError]
  );

  useEffect(() => {
    setPage(0);
    setPageTokens([""]);
    setNextPageToken("");
    fetchData();
  }, [fetchData]);

  const handleChangePage = (_: unknown, newPage: number) => {
    if (newPage > page) {
      if (nextPageToken) {
        setPageTokens((prev) => [...prev, nextPageToken]);
      }
      fetchData(nextPageToken);
    } else {
      fetchData(pageTokens[newPage]);
    }
    setPage(newPage);
  };

  const colSpan = 4;

  return (
    <Grid container>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <b>Company Name</b>
              </TableCell>
              <TableCell>
                <b>Status</b>
              </TableCell>
              <TableCell align="right">
                <b>Users</b>
              </TableCell>
              <TableCell align="right">
                <b>Key Contacts</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && summaries.length === 0 && (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && summaries.length === 0 && (
              <EmptyTableResults
                colSpan={colSpan}
                tableHeading="contractor companies"
              />
            )}
            {summaries.map((summary) => (
              <TableRow key={summary.companyId}>
                <TableCell>{summary.companyName}</TableCell>
                <TableCell>
                  {statusLabel(
                    summary.status ?? ContractorCompanyStatus.UNSPECIFIED
                  )}
                </TableCell>
                <TableCell align="right">{summary.userCount}</TableCell>
                <TableCell align="right">{summary.keyContactCount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          ActionsComponent={RPCTablePaginationActions}
        />
      </TableContainer>
    </Grid>
  );
};

export default OrgContractorsTab;
